import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react'
import axios from 'axios'
import { WritingPrompt } from '../types/models'
import { useAuth } from './AuthContext'

interface PromptContextType {
  prompts: WritingPrompt[]
  selectedPrompt: WritingPrompt | null
  loading: boolean
  error: string | null
  getPrompts: (yearLevel: number, testFormat: 'edutest' | 'acer', category?: string) => Promise<void>
  getRandomPrompt: (yearLevel: number, testFormat: 'edutest' | 'acer', category?: string) => Promise<WritingPrompt | null>
  selectPrompt: (prompt: WritingPrompt | null) => void
  clearPrompts: () => void
}

const PromptContext = createContext<PromptContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export const PromptProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [prompts, setPrompts] = useState<WritingPrompt[]>([])
  const [selectedPrompt, setSelectedPrompt] = useState<WritingPrompt | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated } = useAuth()
  const isFetching = useRef(false)

  const getPrompts = useCallback(async (
    yearLevel: number, 
    testFormat: 'edutest' | 'acer', 
    category?: string
  ) => {
    if (isFetching.current || !isAuthenticated) return
    
    isFetching.current = true
    setLoading(true)
    setError(null)

    try {
      let url = `${API_BASE_URL}/api/prompts?yearLevel=${yearLevel}&testFormat=${testFormat}`
      if (category) {
        url += `&category=${category}`
      }
      
      const response = await axios.get(url)
      const fetchedPrompts = (response.data.prompts || []).map((p: any) => ({
        ...p,
        id: p.id || p.promptId,
        createdAt: p.createdAt ? new Date(p.createdAt) : new Date()
      }))
      setPrompts(fetchedPrompts)
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch prompts'
      setError(errorMessage)
      console.error('Error fetching prompts:', err)
    } finally {
      setLoading(false)
      isFetching.current = false
    }
  }, [isAuthenticated])

  const getRandomPrompt = useCallback(async (
    yearLevel: number,
    testFormat: 'edutest' | 'acer',
    category?: string
  ): Promise<WritingPrompt | null> => {
    if (!isAuthenticated) return null

    setLoading(true)
    setError(null)

    try {
      let url = `${API_BASE_URL}/api/prompts/random?yearLevel=${yearLevel}&testFormat=${testFormat}`
      if (category) {
        url += `&category=${category}`
      }

      const response = await axios.get(url)
      const prompt = response.data.prompt
      if (prompt) {
        return {
          ...prompt,
          id: prompt.id || prompt.promptId,
          createdAt: prompt.createdAt ? new Date(prompt.createdAt) : new Date()
        }
      }
      return null
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch random prompt'
      setError(errorMessage)
      console.error('Error fetching random prompt:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const selectPrompt = useCallback((prompt: WritingPrompt | null) => {
    setSelectedPrompt(prompt)
  }, [])

  const clearPrompts = useCallback(() => {
    setPrompts([])
    setSelectedPrompt(null)
  }, [])

  const value: PromptContextType = {
    prompts,
    selectedPrompt,
    loading,
    error,
    getPrompts,
    getRandomPrompt,
    selectPrompt,
    clearPrompts
  }

  return <PromptContext.Provider value={value}>{children}</PromptContext.Provider>
}

export const usePrompt = () => {
  const context = useContext(PromptContext)
  if (context === undefined) {
    throw new Error('usePrompt must be used within a PromptProvider')
  }
  return context
}
